import React, { memo, useMemo, useState } from 'react';
import mobilePreviewContainer from '@Theme/icons/preview-mobile.png';
import desktopPreviewContainer from '@Theme/icons/preview-web.svg';
import { DesktopOutlined, MobileOutlined } from '@ant-design/icons';
import { Segmented } from 'antd';
import { useSelector } from 'react-redux';
import {
  getDesktopPreviewSize,
  getMobilePreviewSize,
} from '../../onsite/sizing';

/** Horizontal inset so overlay stays inside the phone screen (not the bezel). */
const MOBILE_SCREEN_INSET_X = 36;
const MOBILE_PREVIEW_WIDTH = 440;
const MOBILE_SCREEN_WIDTH = MOBILE_PREVIEW_WIDTH - MOBILE_SCREEN_INSET_X * 2;

/**
 * Backend preview HTML often sets .onsite-text-layout { height:100% } and
 * uses either <div class="onsite-img-container"><img></div> OR
 * <img class="onsite-img-container">. Keep natural aspect ratio (no fixed
 * height + cover) so wide banners (e.g. 560×100) are not cropped.
 */
const ONSITE_PREVIEW_IMAGE_FIX = `
html, body {
  height: auto !important;
  min-height: 0 !important;
  margin: 0 !important;
  /* Allow tall/square creatives to scroll inside the preview iframe. */
  overflow-x: hidden !important;
  overflow-y: visible !important;
}
/* Live templates use position:fixed + top:50% centering; that shifts the
   card out of the preview iframe and crops the banner. Keep in-flow. */
.onsite-text-layout,
.onsite-image-layout,
.onsite-coupon-layout {
  position: relative !important;
  inset: auto !important;
  top: auto !important;
  right: auto !important;
  bottom: auto !important;
  left: auto !important;
  transform: none !important;
  margin-inline: auto !important;
  margin-block: 0 !important;
  height: auto !important;
  max-height: none !important;
}
div.onsite-img-container {
  width: 100% !important;
  height: auto !important;
  max-height: none !important;
  flex-shrink: 0 !important;
  overflow: hidden !important;
  line-height: 0 !important;
}
div.onsite-img-container img {
  width: 100% !important;
  height: auto !important;
  max-height: none !important;
  object-fit: contain !important;
  object-position: center !important;
  display: block !important;
}
img.onsite-img-container,
.onsite-img-container {
  display: block !important;
  width: 100% !important;
  height: auto !important;
  max-height: none !important;
  object-fit: contain !important;
  object-position: center !important;
  flex-shrink: 0 !important;
}
.onsite-image-layout img,
img.onsite-image-layout {
  width: 100% !important;
  height: auto !important;
  object-fit: contain !important;
  object-position: center !important;
}
.container--default img {
  max-width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center;
}
`;

function withOnSitePreviewImageFix(html) {
  if (!html || typeof html !== 'string') return html;
  const tag = `<style data-onsite-preview-img-fix>${ONSITE_PREVIEW_IMAGE_FIX}</style>`;
  if (html.includes('data-onsite-preview-img-fix')) return html;
  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `${tag}</head>`);
  }
  return `${tag}${html}`;
}

const PreviewContainer = ({
  variation,
  previewPage = false,
  themeOptions,
  isShowMultiPlatform = false,
}) => {
  const [previewType, setPreviewType] = useState('mobile');
  const { html = '', configuration } = useSelector(
    (state) => state.engage.communication.communicationPreview
  );

  const theme = {};
  themeOptions?.forEach((item) => {
    theme[item.key] =
      (variation?.theme &&
        item.key in variation?.theme &&
        variation?.theme[item.key]) ||
      item.default;
  });

  // Map configuration.position to preview chrome. Only one vertical edge
  // is active (top OR bottom) so absolute placement matches SDK semantics.
  const styles = useMemo(() => {
    const position = variation?.configuration?.position;
    const isMobile = previewType === 'mobile';
    const isDesktop = previewType === 'desktop';
    const topChrome = 90;
    const bottomChrome = isMobile ? 32 : 16;
    const centerXDesktop = {
      left: '50%',
      right: 'auto',
      transform: 'translateX(-50%)',
    };

    switch (position) {
      case 'CENTER':
        return {
          top: isMobile ? '25%' : topChrome,
          bottom: 'auto',
          ...(isDesktop ? centerXDesktop : {}),
        };

      case 'CENTER_TOP':
        return {
          top: isMobile ? 90 : topChrome,
          bottom: 'auto',
          ...(isDesktop ? centerXDesktop : {}),
        };

      case 'CENTER_BOTTOM':
        return {
          top: 'auto',
          bottom: bottomChrome,
          ...(isDesktop ? centerXDesktop : {}),
        };

      case 'BOTTOM_START':
        return {
          top: 'auto',
          bottom: bottomChrome,
          ...(isDesktop ? { left: 24, right: 'auto', transform: 'none' } : {}),
        };

      case 'BOTTOM_END':
        return {
          top: 'auto',
          bottom: bottomChrome,
          ...(isDesktop ? { left: 'auto', right: 24, transform: 'none' } : {}),
        };

      default:
        return {
          top: topChrome,
          bottom: 'auto',
          ...(isDesktop ? centerXDesktop : {}),
        };
    }
  }, [variation, previewType]);

  const mobilePlatformOptions = [
    {
      value: 'mobile',
      icon: <MobileOutlined />,
    },
    {
      value: 'desktop',
      icon: <DesktopOutlined />,
    },
  ];

  // Prefer live editor configuration when present so size mode changes
  // reflect immediately; fall back to preview API payload.
  const sizeConfig = variation?.configuration || configuration || {};
  const previewHtml = useMemo(() => withOnSitePreviewImageFix(html), [html]);
  const {
    width: desktopWidth,
    maxWidth: desktopMaxWidth,
    height: desktopHeight,
  } = getDesktopPreviewSize(sizeConfig);

  const {
    width: mobileWidth,
    maxWidth: mobileMaxWidth,
    height: mobileHeight,
  } = getMobilePreviewSize(sizeConfig, MOBILE_SCREEN_WIDTH);

  const mobileJustifyContent = (() => {
    const pos = variation?.configuration?.position;
    if (pos === 'BOTTOM_START') return 'flex-start';
    if (pos === 'BOTTOM_END') return 'flex-end';
    // CENTER, CENTER_TOP, CENTER_BOTTOM, default
    return 'center';
  })();

  return (
    <section
      style={{
        backgroundColor: 'transparent',
      }}
      className={`message-container__preview ${
        previewPage ? 'message-container__preview--static' : ''
      }
      ${
        previewPage && previewType === 'desktop' ? 'top-width-fix' : ''
      } overflow-hidden`}
    >
      {isShowMultiPlatform && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingBlock: 8,
          }}
        >
          <Segmented
            style={{ borderRadius: 8 }}
            value={previewType}
            onChange={setPreviewType}
            options={mobilePlatformOptions}
            className="preview-switcher"
          />
        </div>
      )}
      {previewType === 'mobile' ? (
        <div
          className="push-notification push-notification--android"
          style={{
            position: 'relative',
            display: 'block',
            overflow: 'hidden',
            width: MOBILE_PREVIEW_WIDTH,
            marginInline: 'auto',
          }}
        >
          <img
            src={mobilePreviewContainer}
            alt="preview"
            style={{
              width: MOBILE_PREVIEW_WIDTH,
              height: 800,
              display: 'block',
            }}
          />
          <div
            className="push-notification__block"
            style={{
              display: 'flex',
              justifyContent: mobileJustifyContent,
              alignItems: 'flex-start',
              height: 'fit-content',
              // Pin both sides so the screen area width is definite.
              left: MOBILE_SCREEN_INSET_X,
              right: MOBILE_SCREEN_INSET_X,
              width: 'auto',
              maxWidth: MOBILE_SCREEN_WIDTH,
              boxSizing: 'border-box',
              overflow: 'hidden',
              top: styles.top,
              bottom: styles.bottom,
            }}
          >
            {/*
              Wrapper must shrink to the iframe size (fit-content), otherwise
              a full-width wrapper keeps small presets left-aligned and
              justifyContent on the parent has no visible effect.
            */}
            <div
              style={{
                overflow: 'hidden',
                width: mobileWidth,
                maxWidth: mobileMaxWidth,
                boxSizing: 'border-box',
                flex: '0 0 auto',
              }}
            >
              {previewHtml && (
                <iframe
                  srcDoc={previewHtml}
                  seamless
                  id="onSite_mobile_iframe"
                  loading="lazy"
                  frameBorder={0}
                  sandbox="allow-same-origin allow-scripts allow-popups"
                  style={{
                    visibility: previewHtml ? 'visible' : 'hidden',
                    display: 'block',
                    boxSizing: 'border-box',
                    width: '100%',
                    maxWidth: '100%',
                    height: mobileHeight === 'auto' ? undefined : mobileHeight,
                    minHeight: undefined,
                    border: 0,
                  }}
                  onLoad={(event) => {
                    // Size iframe to content so Auto/Preset cards are not
                    // trapped in a tall empty 300px frame.
                    const iframe = event.currentTarget;
                    const resizeToContent = () => {
                      try {
                        const doc = iframe.contentDocument;
                        const root =
                          doc?.querySelector(
                            '.onsite-text-layout, .onsite-image-layout, .onsite-coupon-layout, .container--default'
                          ) || doc?.body;
                        if (!root) return;
                        const h = Math.ceil(
                          root.getBoundingClientRect().height
                        );
                        if (h > 0) {
                          iframe.style.height = `${h}px`;
                        }
                      } catch (_) {
                        // cross-origin / sandbox — keep CSS height
                      }
                    };
                    resizeToContent();
                    requestAnimationFrame(() => {
                      resizeToContent();
                      requestAnimationFrame(resizeToContent);
                    });
                    try {
                      iframe.contentDocument
                        ?.querySelectorAll('img')
                        .forEach((img) => {
                          if (img.complete) {
                            resizeToContent();
                          } else {
                            img.addEventListener('load', resizeToContent, {
                              once: true,
                            });
                          }
                        });
                    } catch (_) {
                      // ignore
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>
      ) : null}
      {previewType === 'desktop' ? (
        <div
          className={`push-notification`}
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            height: '100%',
            width: '100%',
            maxHeight: 'min(72vh, 640px)',
            overflow: 'hidden',
            margin: 'auto',
            position: 'relative',
          }}
        >
          <img
            src={desktopPreviewContainer}
            alt="preview"
            style={{
              height: 'auto',
              maxHeight: 'min(72vh, 640px)',
              width: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />

          {previewHtml && (
            <iframe
              srcDoc={previewHtml}
              seamless
              loading="lazy"
              id="onSite_desktop_iframe"
              sandbox="allow-same-origin allow-scripts allow-popups"
              frameBorder={0}
              style={{
                margin: 0,
                visibility: previewHtml ? 'visible' : 'hidden',
                position: 'absolute',
                width: desktopWidth,
                maxWidth: desktopMaxWidth,
                // Tall creatives (e.g. square) may exceed the mock screen —
                // scroll inside the iframe instead of clipping.
                maxHeight: 'calc(100% - 106px)',
                overflowX: 'hidden',
                overflowY: 'auto',
                height: desktopHeight === 'auto' ? undefined : desktopHeight,
                border: 0,
                ...styles,
              }}
              onLoad={(event) => {
                if (desktopHeight !== 'auto') return;
                const iframe = event.currentTarget;
                const resizeToContent = () => {
                  try {
                    const doc = iframe.contentDocument;
                    const root =
                      doc?.querySelector(
                        '.onsite-text-layout, .onsite-image-layout, .onsite-coupon-layout, .container--default'
                      ) || doc?.body;
                    if (!root) return;
                    const contentH = Math.ceil(
                      root.getBoundingClientRect().height
                    );
                    if (contentH <= 0) return;
                    const parentH = iframe.parentElement?.clientHeight || 0;
                    const available =
                      parentH > 0 ? Math.max(120, parentH - 106) : contentH;
                    // Fit short cards; for tall/square cards fill the mock
                    // screen and let the iframe scroll to the rest.
                    if (contentH <= available) {
                      iframe.style.height = `${contentH}px`;
                      iframe.style.overflowY = 'hidden';
                    } else {
                      iframe.style.height = `${available}px`;
                      iframe.style.overflowY = 'auto';
                    }
                  } catch (_) {
                    // ignore
                  }
                };
                // Defer: backend/Advanced CSS often sets height:100% / fixed
                // px first; measure after our contain/auto overrides settle.
                resizeToContent();
                requestAnimationFrame(() => {
                  resizeToContent();
                  requestAnimationFrame(resizeToContent);
                });
                setTimeout(resizeToContent, 50);
                setTimeout(resizeToContent, 200);
                try {
                  iframe.contentDocument
                    ?.querySelectorAll('img')
                    .forEach((img) => {
                      if (img.complete) {
                        resizeToContent();
                      } else {
                        img.addEventListener('load', resizeToContent, {
                          once: true,
                        });
                      }
                    });
                } catch (_) {
                  // ignore
                }
              }}
            />
          )}
        </div>
      ) : null}
    </section>
  );
};

const Memoized = memo(PreviewContainer);

export { Memoized as PreviewContainer };
